"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import DocsPageFrame from "../../../components/DocsPageFrame";
import {
  Landmark,
  Database,
  ClipboardCheck,
  BookOpen,
  ShoppingCart,
  Calculator,
  Boxes,
  FileText,
  BarChart3,
  CheckCircle,
  Settings,
  Headphones,
  LayoutDashboard,
  ArrowRight,
} from "lucide-react";
import Link from "next/link";

const MODULES = [
  {
    icon: LayoutDashboard,
    color: "#171717",
    bg: "#F5F5F5",
    name: "Home",
    desc: "Dashboard — General, Sales, Credit Limit, Individual Work Tracking, Getting Started.",
    href: "/documentation/books/home/dashboard",
  },
  {
    icon: Landmark,
    color: "#1785B6",
    bg: "#EFF8FF",
    name: "Banking",
    desc: "Banks and Bank Transfer Logs. Money posts after approval.",
    href: "/documentation/books/banking/banks",
  },
  {
    icon: Database,
    color: "#7C3AED",
    bg: "#F5F3FF",
    name: "All Master",
    desc: "Item, Quality, Division, Branch, Warehouse, Process, Recipe, Price List, Item Group, ManPower, Sales Order Tag.",
    href: "/documentation/books/all-master/item-master",
  },
  {
    icon: ClipboardCheck,
    color: "#059669",
    bg: "#ECFDF5",
    name: "Production",
    desc: "Add Production and Submit Production.",
    href: "/documentation/books/production/add-production",
  },
  {
    icon: BookOpen,
    color: "#DF5D01",
    bg: "#FFF7F2",
    name: "Sales",
    desc: "Customer → Quotes → Customer Orders → Sales Orders → Delivery Challans → Invoices → Payments Received. Also Recurring Invoices and Credit Notes.",
    href: "/documentation/books/sales/invoices",
  },
  {
    icon: ShoppingCart,
    color: "#1785B6",
    bg: "#EFF8FF",
    name: "Purchase",
    desc: "Vendors → Purchase Orders → GRPO → Bills → Payments Made. Also Expenses, Recurring Bills / Expenses, Debit Notes.",
    href: "/documentation/books/purchase/bills",
  },
  {
    icon: Calculator,
    color: "#7C3AED",
    bg: "#F5F3FF",
    name: "Accountant",
    desc: "Chart of Accounts, Account Ledger, Manual Journals.",
    href: "/documentation/books/accountant/chart-of-accounts",
  },
  {
    icon: Boxes,
    color: "#059669",
    bg: "#ECFDF5",
    name: "Inventory",
    desc: "View Inventory, Inventory Transfer, Inventory Adjustment.",
    href: "/documentation/books/inventory/view-inventory",
  },
  {
    icon: FileText,
    color: "#1785B6",
    bg: "#EFF8FF",
    name: "Reports",
    desc: "Ledgers, Gross Profit, and Aging.",
    href: "/documentation/books/reports/reports-overview",
  },
  {
    icon: BarChart3,
    color: "#7C3AED",
    bg: "#F5F3FF",
    name: "Analytics",
    desc: "28 named analytics. Not the Home Dashboard.",
    href: "/documentation/books/analytics/analytics-overview",
  },
  {
    icon: Settings,
    color: "#171717",
    bg: "#F5F5F5",
    name: "Settings",
    desc: "Gear in the header — Profile, Users, Roles, GST portal, Inventory Config, Credit Limits, and the rest.",
    href: "/documentation/books/settings/my-profile",
  },
  {
    icon: Headphones,
    color: "#171717",
    bg: "#F5F5F5",
    name: "Support",
    desc: "Header headphones — not on the left menu.",
    href: "/documentation/books/support/support",
  },
];

const sections = [
  { heading: "Who uses it" },
  { heading: "The modules" },
];

export default function WelcomePage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.1 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[32px] sm:text-[40px] md:text-[46px] leading-[1.15] text-[#171717] sentient-medium mb-5">
            Welcome to ABSS Books
          </h1>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#525252] geist-regular max-w-2xl">
            Cloud accounting and inventory for Indian GST businesses. Sales,
            purchase, production, and banking post to one live ledger.
          </p>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="mb-12 p-5 bg-[#F9F9F9] border border-[#E5E5E5] rounded-[14px]"
        >
          <h2
            id="section-0"
            className="text-[18px] sm:text-[20px] sentient-medium text-[#171717] mb-3"
          >
            Who uses it
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              {
                role: "Traders and distributors",
                detail: "Sales, purchase, GST",
              },
              {
                role: "Manufacturing",
                detail: "Production and stock",
              },
              {
                role: "Retail and wholesale",
                detail: "Multi-branch, multi-warehouse",
              },
              {
                role: "Accountants",
                detail: "Live general ledger",
              },
            ].map((item) => (
              <div key={item.role} className="flex items-start gap-2.5">
                <CheckCircle className="w-4 h-4 text-[#DF5D01] mt-0.5 flex-shrink-0" />
                <p className="text-[13px] geist-regular text-[#525252]">
                  <span className="geist-medium text-[#171717]">
                    {item.role}
                  </span>
                  {" — "}
                  {item.detail}
                </p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div variants={fadeUp} className="mb-10">
          <h2
            id="section-1"
            className="text-[22px] sm:text-[26px] sentient-medium text-[#171717] mb-2"
          >
            The modules
          </h2>
          <p className="text-[14px] geist-regular text-[#525252] mb-6">
            Left-menu order. Settings is the header gear. Support is the header
            headphones.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {MODULES.map((mod) => {
              const Icon = mod.icon;
              return (
                <Link
                  key={mod.name}
                  href={mod.href}
                  className="group border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div
                      className="w-8 h-8 rounded-[8px] flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: mod.bg }}
                    >
                      <Icon className="w-4 h-4" style={{ color: mod.color }} />
                    </div>
                    <h3 className="text-[15px] sentient-medium text-[#171717]">
                      {mod.name}
                    </h3>
                  </div>
                  <p className="text-[12px] geist-regular text-[#525252] leading-relaxed">
                    {mod.desc}
                  </p>
                </Link>
              );
            })}
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/getting-started/setting-up-your-account"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black mb-1 flex items-center gap-1.5">
              Setting Up Your Account <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Sign-up, trial, Getting Started checklist
            </p>
          </Link>
          <Link
            href="/documentation/books/getting-started/finding-your-way-around"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black mb-1 flex items-center gap-1.5">
              Finding Your Way Around <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Header, ⌘K, Settings groups
            </p>
          </Link>
          <Link
            href="/documentation/books/getting-started/about"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black mb-1 flex items-center gap-1.5">
              About ABSS Books <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              GST on documents, and how CRM shares masters
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
